import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ListingCard from '@/components/ListingCard';
import AdBanner from '@/components/AdBanner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ShoppingBag, Gift, Repeat, ArrowRight, Search, MapPin, Users, Shield } from 'lucide-react';

export default function HomePage() {
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/listings/featured').then(r => setFeatured(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = [
    { key: 'sale', label: 'For Sale', icon: ShoppingBag, desc: 'Buy items from your neighbors', color: 'bg-primary/10 text-primary', iconColor: 'text-primary' },
    { key: 'free', label: 'Free Items', icon: Gift, desc: 'Give and receive freely', color: 'bg-[#E5B66A]/10 text-[#E5B66A]', iconColor: 'text-[#E5B66A]' },
    { key: 'lending', label: 'Lending', icon: Repeat, desc: 'Borrow what you need', color: 'bg-[#7D9B8B]/10 text-[#7D9B8B]', iconColor: 'text-[#7D9B8B]' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/96214a60-402d-4ee5-b337-28adb7c2e517/images/671d4a8b13686bfef112d1fdcd77994b0c48891c66ef920164708d4fe1683785.png"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C362F]/90 via-[#2C362F]/70 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 md:py-32">
          <div className="max-w-2xl animate-slide-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Neighborhood<br />Marketplace
            </h1>
            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-lg">
              Buy, sell, lend, and share with people in your community. Build trust through local connections.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/browse" data-testid="hero-browse-btn">
                <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-white px-8 gap-2 h-12 text-base">
                  <Search className="w-5 h-5" strokeWidth={1.5} />Browse Items
                </Button>
              </Link>
              {!user || !user.user_id ? (
                <Link to="/register" data-testid="hero-signup-btn">
                  <Button size="lg" variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 px-8 h-12 text-base">
                    Get Started
                  </Button>
                </Link>
              ) : (
                <Link to="/create-listing" data-testid="hero-post-btn">
                  <Button size="lg" variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 px-8 h-12 text-base">
                    Post a Listing
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-24">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-bold text-[#2C362F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What are you looking for?
          </h2>
          <p className="text-base text-muted-foreground max-w-md mx-auto">Browse by category to find exactly what you need from your community</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {categories.map((cat, i) => (
            <Link
              key={cat.key}
              to={`/browse?category=${cat.key}`}
              className="group"
              data-testid={`category-${cat.key}-btn`}
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6 hover:shadow-md transition-all duration-300 hover:-translate-y-1 h-full">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${cat.color}`}>
                  <cat.icon className={`w-7 h-7 ${cat.iconColor}`} strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold text-[#2C362F] mb-2 group-hover:text-primary transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {cat.label}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{cat.desc}</p>
                <div className="mt-4 flex items-center text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  Browse <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Ad Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-8">
        <AdBanner placement="homepage" />
      </div>

      {/* Featured Listings */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16 md:pb-24">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#2C362F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Featured Listings
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Popular items from your community</p>
          </div>
          <Link to="/browse" data-testid="view-all-listings-btn">
            <Button variant="ghost" className="rounded-full gap-1">
              View All <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white border border-[#E8E6DF] rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-muted" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-full" />
                  <div className="h-5 bg-muted rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : featured.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="featured-listings">
            {featured.map(listing => (
              <ListingCard key={listing.listing_id} listing={listing} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white border border-[#E8E6DF] rounded-2xl">
            <img src="https://images.unsplash.com/photo-1617611313649-fbddfaa37b52?w=200&h=200&fit=crop" alt="" className="w-24 h-24 rounded-2xl object-cover mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground mb-4">No listings yet. Be the first to post!</p>
            <Link to="/create-listing">
              <Button className="rounded-full bg-primary text-white">Post a Listing</Button>
            </Link>
          </div>
        )}
      </section>

      {/* Trust Section */}
      <section className="bg-[#2C362F] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why BorrowTools?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: MapPin, title: 'Hyper-Local', desc: 'Find items and people right in your neighborhood' },
              { icon: Users, title: 'Community Built', desc: 'Ratings and reviews keep transactions trustworthy' },
              { icon: Shield, title: 'Safe & Moderated', desc: 'Report tools and admin moderation protect everyone' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
